import { createContext, useContext, useState, useEffect } from 'react';

// Supported currencies with their symbols and formatting
const CURRENCIES = {
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    locale: 'en-GB',
    countries: ['GB', 'UK'],
    decimalPlaces: 2,
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    locale: 'de-DE', // Default to German locale for Euro
    countries: [
      'AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 
      'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES'
    ],
    decimalPlaces: 2,
  },
};

// Exchange rates (relative to GBP as base)
// In production, fetch from an API like exchangerate-api.com
const EXCHANGE_RATES = {
  GBP: 1.0,
  EUR: 1.17, // 1 GBP = 1.17 EUR (approximate)
};

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => {
    // Try to get from localStorage first
    const saved = localStorage.getItem('hambre_currency');
    if (saved && CURRENCIES[saved]) {
      return saved;
    }
    // Default to GBP
    return 'GBP';
  });

  // Save currency preference to localStorage
  useEffect(() => {
    localStorage.setItem('hambre_currency', currency);
  }, [currency]);

  // Get currency config
  const getCurrencyConfig = () => CURRENCIES[currency] || CURRENCIES.GBP;

  // Format price in current currency
  const formatPrice = (amount, options = {}) => {
    const config = getCurrencyConfig();
    const { showCode = false, convertFrom = null } = options;

    // Convert if needed
    let finalAmount = amount;
    if (convertFrom && convertFrom !== currency) {
      // Convert from source currency to current currency
      const sourceRate = EXCHANGE_RATES[convertFrom] || 1;
      const targetRate = EXCHANGE_RATES[currency] || 1;
      finalAmount = (amount / sourceRate) * targetRate;
    }

    // Format the number
    const formatted = new Intl.NumberFormat(config.locale, {
      minimumFractionDigits: config.decimalPlaces,
      maximumFractionDigits: config.decimalPlaces,
    }).format(finalAmount);

    // Return with symbol
    if (showCode) {
      return `${config.symbol}${formatted} ${config.code}`;
    }
    return `${config.symbol}${formatted}`;
  };

  // Convert amount between currencies
  const convertCurrency = (amount, fromCurrency, toCurrency) => {
    const fromRate = EXCHANGE_RATES[fromCurrency] || 1;
    const toRate = EXCHANGE_RATES[toCurrency] || 1;
    return (amount / fromRate) * toRate;
  };

  // Get all available currencies
  const getAvailableCurrencies = () => Object.values(CURRENCIES);

  // Detect currency from country code
  const detectCurrencyFromCountry = (countryCode) => {
    const upperCode = countryCode?.toUpperCase();
    for (const [currCode, config] of Object.entries(CURRENCIES)) {
      if (config.countries.includes(upperCode)) {
        return currCode;
      }
    }
    return 'GBP'; // Default
  };

  const value = {
    currency,
    setCurrency,
    currencyConfig: getCurrencyConfig(),
    formatPrice,
    convertCurrency,
    getAvailableCurrencies,
    detectCurrencyFromCountry,
    CURRENCIES,
    EXCHANGE_RATES,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

// Utility component for displaying prices
export function Price({ amount, convertFrom = null, showCode = false, className = '' }) {
  const { formatPrice } = useCurrency();
  return (
    <span className={className}>
      {formatPrice(amount, { convertFrom, showCode })}
    </span>
  );
}

export default CurrencyContext;
